import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface GridPaginationProps {
  page: number;
  totalPages: number;
  total?: number;
  label?: string;
  loading?: boolean;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * The sizes on offer, always including the one in force.
 *
 * A page size does not have to be one of the four above: a company can set any
 * default in its shipping config, and a grid can choose its own (Facility
 * Shipments loads 25). A select whose value matches none of its options shows
 * its first one, so without this the footer read "Show 10" over 25 rows.
 */
const pageSizeOptions = (limit: number) =>
  PAGE_SIZE_OPTIONS.includes(limit) || !(limit > 0)
    ? PAGE_SIZE_OPTIONS
    : [...PAGE_SIZE_OPTIONS, limit].sort((a, b) => a - b);

/**
 * Shared pagination bar used across all grid pages.
 * Shows: total count · page info · prev/next buttons · optional per-page selector
 */
const GridPagination: React.FC<GridPaginationProps> = ({
  page,
  totalPages,
  total,
  label = 'records',
  loading = false,
  limit,
  onPageChange,
  onLimitChange,
}) => {
  // A grid restores its last page from the user's bookmark; if the data has
  // shrunk since, that page is past the end. Step back to the last real page
  // once the data is in - before that, totals are still the initial zeros.
  useEffect(() => {
    if (loading || !total || totalPages < 1 || page <= totalPages) return;
    onPageChange(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, total, totalPages, page]);

  return (
    <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between bg-slate-50 flex-wrap gap-3">
      {/* Left: total */}
      <div className="flex items-center gap-4">
        {total !== undefined && (
          <span className="text-xs text-slate-600 font-bold uppercase tracking-[0.15em]">
            {total.toLocaleString()} {label}
          </span>
        )}
        {onLimitChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500 font-medium">Show</span>
            <select
              value={limit}
              onChange={(e) => {
                onLimitChange(parseInt(e.target.value, 10));
                onPageChange(1);
              }}
              className="text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer"
            >
              {pageSizeOptions(limit).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: page info + nav */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-600 font-medium">
          Page <span className="font-bold text-slate-800">{page}</span> of{' '}
          <span className="font-bold text-slate-800">{totalPages}</span>
        </span>
        <div className="flex gap-1">
          <button
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-30 transition-all shadow-sm"
            title="Previous page"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            disabled={page >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
            className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-30 transition-all shadow-sm"
            title="Next page"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GridPagination;
